/**
 * Wraps `GET /me/gamification/leaderboard`. Soft-gated — disabled tenants
 * still get a 200 with empty data; the FE flag `gamificationEnabled` lets
 * callers hide the surface entirely.
 *
 * Note: the backend does not currently support time-window filtering. The
 * dashboard tabs ("Top 7 days / 30 days / All times") are visual-only.
 */

import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { getLeaderboard, getPoints } from '../../api/me';
import type { LeaderboardEntry } from '../../types/learnerPrizes';

interface UseLeaderboardResult {
  entries: LeaderboardEntry[];
  currentRank: LeaderboardEntry | null;
  isLoading: boolean;
  isError: boolean;
  gamificationEnabled: boolean;
}

export function useLeaderboard(perPage = 10): UseLeaderboardResult {
  const points = useQuery({
    queryKey: ['me', 'gamification', 'points'],
    queryFn: getPoints,
  });

  const enabled = points.data?.enabled !== false;

  const board = useQuery({
    queryKey: ['me', 'gamification', 'leaderboard', perPage],
    queryFn: () => getLeaderboard({ per_page: perPage, page: 1 }),
    enabled,
    retry: (count, err) => {
      if (isAxiosError(err) && err.response?.status === 403) return false;
      return count < 2;
    },
  });

  const entries = board.data?.data ?? [];
  const currentRank = entries.find((e) => e.is_me) ?? null;

  return {
    entries,
    currentRank,
    isLoading: points.isLoading || (enabled && board.isLoading),
    isError: points.isError || board.isError,
    gamificationEnabled: enabled,
  };
}
